import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isSellerEntity, SellerEntity } from '@crm/shared'
import { useNavigate } from 'react-router-dom'
import { getRequest } from '../../../shared/api/requests.api'
import type { RequestType } from '../../../shared/api/types'
import { makeEmptyRow, makeRowUid, type ItemRow } from '../components/ItemsTable'
import type { ParsedLineStatus } from '../utils/mapIntakeResult'
import type { ClientOption } from '../components/ClientCombobox'

/** Сеттеры формы «Новая заявка», которые заполняются из черновика. */
export interface DraftFormSetters {
  setClientId: (v: string) => void
  /** Клиент черновика целиком — поле «Клиент» показывает его без поиска по списку. */
  setClient: (v: ClientOption) => void
  setType: (v: RequestType) => void
  setSellerEntityId: (v: SellerEntity) => void
  setDeadline: (v: string) => void
  setNotes: (v: string) => void
  setOwnerManagerId: (v: string) => void
  setRawText: (v: string) => void
  setParsedLines: (v: ParsedLineStatus[]) => void
  setItems: (v: ItemRow[]) => void
}

// Загрузка существующего черновика — один раз при монтировании. Вынесено из
// NewRequestPage (≤500 строк), логика не менялась.
//
// staleTime: 0 + refetchOnMount: 'always' — всегда тянем свежую версию,
// даже если TanStack-кэш ещё помнит старую (иначе эффект ниже
// инициализировал форму стейлом и выставлял draftLoaded до прихода
// свежих данных → правки «запаздывали на одно действие»).
// setters должны быть стабильными (сеттеры useState, собранные в useMemo).
export function useDraftLoader(
  draftId: string | null,
  isAssistant: boolean,
  setters: DraftFormSetters,
) {
  const navigate = useNavigate()
  const [draftLoaded, setDraftLoaded] = useState(false)

  const { data: draftRequest, isFetching: isDraftFetching } = useQuery({
    queryKey: ['requests', draftId ?? 'none'],
    queryFn: () => (draftId ? getRequest(draftId) : Promise.resolve(null)),
    enabled: !!draftId,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  useEffect(() => {
    // Ждём пока запрос реально отработает (а не вернёт stale из кэша).
    if (!draftRequest || draftLoaded || isDraftFetching) return
    if (draftRequest.status !== 'DRAFT') {
      void navigate(`/requests/${draftRequest.id}`, { replace: true })
      return
    }
    setters.setClientId(draftRequest.clientId)
    if (draftRequest.client) setters.setClient(draftRequest.client)
    setters.setType(draftRequest.type)
    // Юрлицо продажи черновика; у черновиков до 23.09 его нет — по умолчанию.
    setters.setSellerEntityId(
      isSellerEntity(draftRequest.sellerEntityId) ? draftRequest.sellerEntityId : SellerEntity.SPM,
    )
    setters.setDeadline(draftRequest.deadline ? draftRequest.deadline.slice(0, 10) : '')
    setters.setNotes(draftRequest.notes ?? '')
    // Черновик помощника уже привязан к менеджеру — восстанавливаем выбор.
    if (isAssistant && draftRequest.managerId) setters.setOwnerManagerId(draftRequest.managerId)
    // Восстанавливаем контекст парсинга: исходный текст вставки + аннотированные
    // строки. Это позволяет при переоткрытии черновика видеть и textarea, и блок
    // «Результат распознавания» в том же виде, в котором он был сохранён.
    const meta = draftRequest.intakeMeta
    if (meta?.rawText) setters.setRawText(meta.rawText)
    if (meta?.parsedLines && meta.parsedLines.length > 0) setters.setParsedLines(meta.parsedLines)
    const rows: ItemRow[] = (draftRequest.items ?? []).map((it, idx) => ({
      uid: makeRowUid(),
      partNumber: it.partNumber,
      customerPartNumber: it.customerPartNumber ?? it.partNumber,
      brand: it.brand ?? '',
      quantity: it.quantity,
      unit: it.unit ?? 'pcs',
      note: it.noteForProcurement ?? '',
      priority: it.priority,
      matrix: it.matrix ?? '',
      category: it.category ?? '',
      checked: true,
      lineNumber: idx + 1,
      needsManualReview: false,
      parseWarnings: [],
    }))
    setters.setItems(rows.length > 0 ? rows : [makeEmptyRow()])
    setDraftLoaded(true)
  }, [draftRequest, draftLoaded, isDraftFetching, navigate, isAssistant, setters])

  return { draftLoaded, setDraftLoaded }
}
